import CloseIcon from "@mui/icons-material/Close"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    LinearProgress,
    Stack,
    Typography,
} from "@mui/material"
import React, {useState} from "react"
import PropTypes from "prop-types"

import {workflowStatusPresentation} from "./workflow-execution"

const formatDate = (value) => value ? new Date(value).toLocaleString("pt-BR") : "N/A"

const errorShape = PropTypes.shape({
    id: PropTypes.number,
    fingerprint: PropTypes.string,
    code: PropTypes.string,
    error_type: PropTypes.string,
    message: PropTypes.string,
    details: PropTypes.object,
    retryable: PropTypes.bool,
})

const Diagnostic = ({error}) => {
    if (!error) return null
    const details = error.details || {}
    return (
        <Box sx={{border: "1px solid", borderColor: "error.light", borderRadius: 1, p: 2}}>
            <Stack direction="row" spacing={1} sx={{mb: 1, flexWrap: "wrap"}}>
                <Chip size="small" color="error" label={error.code || error.error_type || "ERRO"}/>
                <Chip
                    size="small"
                    variant="outlined"
                    label={error.retryable ? "Retentável" : "Definitivo"}
                />
            </Stack>
            <Typography variant="body2" sx={{wordBreak: "break-word"}}>{error.message || "Sem mensagem"}</Typography>
            {(details.source || details.origin || details.curve) && (
                <Typography variant="caption" display="block" sx={{mt: 1}}>
                    Origem: {details.source || details.origin || "N/A"} · Curva: {details.curve || "N/A"}
                </Typography>
            )}
            {Object.keys(details).length > 0 && (
                <Box component="pre" sx={{fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-word", mb: 0}}>
                    {JSON.stringify(details, null, 2)}
                </Box>
            )}
        </Box>
    )
}

Diagnostic.propTypes = {
    error: errorShape.isRequired,
}

const WorkflowExecutionDrawer = ({open, execution, loading, onClose}) => {
    const [copied, setCopied] = useState(false)
    const status = workflowStatusPresentation(execution?.status)

    const copyDiagnostic = async () => {
        if (!execution) return
        const diagnostic = {
            execution_id: execution.id,
            dag_id: execution.dag_id,
            run_id: execution.run_id,
            status: execution.status,
            current_stage: execution.current_stage,
            current_error: execution.current_error,
        }
        await navigator.clipboard.writeText(JSON.stringify(diagnostic, null, 2))
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{width: {xs: "100vw", sm: 580}, p: 3}}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Detalhes da execução</Typography>
                    <IconButton aria-label="Fechar detalhes" onClick={onClose}><CloseIcon/></IconButton>
                </Stack>
                <Divider sx={{my: 2}}/>
                {loading && <Box sx={{display: "flex", justifyContent: "center", p: 5}}><CircularProgress/></Box>}
                {!loading && execution && (
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1} sx={{flexWrap: "wrap"}}>
                            <Chip label={execution.contract_version.toUpperCase()} color={execution.contract_version === "v2" ? "secondary" : "default"}/>
                            <Chip label={status.text} color={status.color}/>
                        </Stack>
                        <Box>
                            <Typography variant="subtitle1">{execution.dag_id}</Typography>
                            <Typography variant="caption" sx={{wordBreak: "break-all"}}>{execution.run_id || "Run não informado"}</Typography>
                        </Box>
                        <Box>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2">Progresso</Typography>
                                <Typography variant="body2">{execution.progress_processed}/{execution.progress_total} ({execution.progress_percent}%)</Typography>
                            </Stack>
                            <LinearProgress variant="determinate" value={execution.progress_percent}/>
                        </Box>
                        <Typography variant="body2">Etapa atual: {execution.current_stage || "Não informada"}</Typography>
                        <Typography variant="body2">Usuário: {execution.created_by_name || execution.created_by || "Não informado"}</Typography>
                        <Typography variant="body2">Heartbeat: {formatDate(execution.heartbeat_at)}</Typography>
                        {execution.current_error && <Diagnostic error={execution.current_error}/>} 
                        <Stack direction="row" spacing={1}>
                            {execution.current_error && (
                                <Button size="small" variant="outlined" startIcon={<ContentCopyIcon/>} onClick={copyDiagnostic}>
                                    {copied ? "Copiado" : "Copiar diagnóstico"}
                                </Button>
                            )}
                            {execution.airflow_url && (
                                <Button size="small" variant="outlined" startIcon={<OpenInNewIcon/>} href={execution.airflow_url} target="_blank" rel="noreferrer">
                                    Abrir Airflow
                                </Button>
                            )}
                        </Stack>
                        <Divider/>
                        {!execution.capabilities.detailed_progress ? (
                            <Typography color="text.secondary">
                                Detalhamento por etapa não disponível para este workflow legado.
                            </Typography>
                        ) : (
                            <>
                                <Typography variant="subtitle1">Etapas e tarefas</Typography>
                                {execution.stages.length === 0 && <Typography color="text.secondary">Nenhuma etapa registrada.</Typography>}
                                {execution.stages.map((stage) => {
                                    const stageStatus = workflowStatusPresentation(stage.status)
                                    return (
                                        <Box key={`${stage.execution_key}-${stage.map_index ?? "none"}`} sx={{border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5}}>
                                            <Stack direction="row" justifyContent="space-between" spacing={1}>
                                                <Typography variant="body2" sx={{fontWeight: 600}}>{stage.task_id || stage.execution_key}</Typography>
                                                <Chip size="small" label={stageStatus.text} color={stageStatus.color}/>
                                            </Stack>
                                            <Typography variant="caption" display="block">
                                                Tentativa {(stage.attempt ?? 0) + 1}
                                                {stage.map_index != null ? ` · Item ${stage.map_index}` : ""}
                                                {stage.duration_seconds != null ? ` · ${stage.duration_seconds}s` : ""}
                                            </Typography>
                                            {(stage.source || stage.curve) && (
                                                <Typography variant="caption" display="block">Origem: {stage.source || "N/A"} · Curva: {stage.curve || "N/A"}</Typography>
                                            )}
                                            <Typography variant="caption" display="block">{formatDate(stage.started_at)} → {formatDate(stage.finished_at)}</Typography>
                                        </Box>
                                    )
                                })}
                            </>
                        )}
                        {execution.errors.length > 0 && (
                            <>
                                <Divider/>
                                <Typography variant="subtitle1">Histórico de erros</Typography>
                                {execution.errors.map((error) => <Diagnostic key={error.id || error.fingerprint} error={error}/>)}
                            </>
                        )}
                    </Stack>
                )}
            </Box>
        </Drawer>
    )
}

WorkflowExecutionDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    loading: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    execution: PropTypes.shape({
        id: PropTypes.number,
        dag_id: PropTypes.string,
        run_id: PropTypes.string,
        contract_version: PropTypes.string,
        status: PropTypes.string,
        current_stage: PropTypes.string,
        progress_processed: PropTypes.number,
        progress_total: PropTypes.number,
        progress_percent: PropTypes.number,
        created_by_name: PropTypes.string,
        created_by: PropTypes.number,
        heartbeat_at: PropTypes.string,
        current_error: errorShape,
        airflow_url: PropTypes.string,
        capabilities: PropTypes.shape({
            detailed_progress: PropTypes.bool,
        }),
        stages: PropTypes.arrayOf(PropTypes.shape({
            execution_key: PropTypes.string,
            task_id: PropTypes.string,
            map_index: PropTypes.number,
            attempt: PropTypes.number,
            status: PropTypes.string,
            duration_seconds: PropTypes.number,
            source: PropTypes.string,
            curve: PropTypes.string,
            started_at: PropTypes.string,
            finished_at: PropTypes.string,
        })),
        errors: PropTypes.arrayOf(errorShape),
    }),
}

export default WorkflowExecutionDrawer
